import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Button,
  Divider,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Business,
  ArrowBack,
  Comment,
  ThumbUp,
  Reply
} from '@mui/icons-material';
import { fetchDiscussionDetail, transformDiscussionData } from '../../Utils/leetCodeApi';

// Local storage keys for discussion details
const STORAGE_KEYS = {
  DISCUSSION_DETAILS: 'leetcode_discussion_details',
  CACHE_TIMESTAMP: 'leetcode_cache_timestamp'
};

// Cache expiration time (24 hours in milliseconds)
const CACHE_EXPIRY = 24 * 60 * 60 * 1000;

export default function DiscussionDetail() {
  const { discussionId } = useParams();
  const navigate = useNavigate();
  const [discussion, setDiscussion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDiscussion = async () => {
      if (!discussionId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Try to load from cache first
        const cachedDiscussion = loadFromCache(discussionId);
        if (cachedDiscussion) {
          setDiscussion(cachedDiscussion);
          setLoading(false);
          return;
        }

        // If not in cache, fetch from API
        const leetCodeData = await fetchDiscussionDetail(discussionId);
        if (leetCodeData) {
          const transformedDiscussion = transformDiscussionData(leetCodeData);
          setDiscussion(transformedDiscussion);
          
          // Save to cache
          saveToCache(discussionId, transformedDiscussion);
        } else {
          setError('Discussion not found');
        }
      } catch (error) {
        console.error('Error fetching discussion:', error);
        setError('Failed to fetch discussion. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDiscussion();
  }, [discussionId]);

  // Load discussion from cache
  const loadFromCache = (id) => {
    try {
      const cacheKey = `${STORAGE_KEYS.DISCUSSION_DETAILS}_${id}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is still valid
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < CACHE_EXPIRY) {
          return parsed.data;
        } else {
          // Cache expired, remove it
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (error) {
      console.error('Error loading from cache:', error);
    }
    return null;
  };

  // Save discussion to cache
  const saveToCache = (id, data) => {
    try {
      const cacheKey = `${STORAGE_KEYS.DISCUSSION_DETAILS}_${id}`;
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  };

  // Clear cache for this discussion
  const clearCache = () => {
    try {
      const cacheKey = `${STORAGE_KEYS.DISCUSSION_DETAILS}_${discussionId}`;
      localStorage.removeItem(cacheKey);
      
      // Reload the discussion
      window.location.reload();
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return "1 day ago";
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: '20px', textAlign: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#008080' }} />
        <Typography variant="h6" sx={{ mt: 2, color: '#666' }}>
          Loading discussion...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: '20px' }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/discussions')}
          sx={{ color: '#008080' }}
        >
          Back to Discussions
        </Button>
      </Container>
    );
  }

  if (!discussion) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: '20px' }}>
        <Typography>Discussion not found</Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/discussions')}
          sx={{ color: '#008080', mt: 2 }}
        >
          Back to Discussions
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ marginTop: '20px', padding: '0 20px' }}>
      {/* Back Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/discussions')}
          sx={{ 
            color: '#008080', 
            '&:hover': {
              backgroundColor: '#e3f2fd'
            }
          }}
        >
          Back to Discussions
        </Button>
      </Box>

      {/* Main Discussion Card with Full Content */}
      <Card sx={{ 
        borderRadius: '12px', 
        mb: 4,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid #f0f0f0'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ 
                fontWeight: 'bold', 
                color: '#008080',
                mb: 3,
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}>
                {discussion.title}
              </Typography>
              
              {/* Full Content Display */}
              {discussion.content && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="body1" sx={{ 
                    color: '#333', 
                    lineHeight: 1.8,
                    fontSize: '1.1rem',
                    textAlign: 'justify',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {discussion.content}
                  </Typography>
                </Box>
              )}
              
              {/* Fallback to description if no content */}
              {!discussion.content && discussion.description && (
                <Typography variant="body1" sx={{ 
                  color: '#333', 
                  mb: 4,
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                  textAlign: 'justify'
                }}>
                  {discussion.description}
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                {discussion.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="medium"
                    sx={{
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      '&:hover': {
                        backgroundColor: '#bbdefb'
                      }
                    }}
                  />
                ))}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ 
                    width: 40, 
                    height: 40, 
                    fontSize: '1rem',
                    backgroundColor: '#008080'
                  }}>
                    {discussion.authorAvatar}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ color: '#333', fontWeight: 'bold' }}>
                      {discussion.author}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      {formatDate(discussion.createdAt)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Comment sx={{ fontSize: 20, color: '#999' }} />
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    {discussion.comments} comments
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Chip
              icon={<Business />}
              label={discussion.company}
              sx={{
                backgroundColor: '#f8f9fa',
                color: '#008080',
                fontWeight: 'bold',
                fontSize: '1rem',
                border: '1px solid #e0e0e0',
                '&:hover': {
                  backgroundColor: '#e3f2fd'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
